<template>
  <b-container v-if="modifier">
    <b-row align-h="start" class="mb-4">
      <b-overlay :show="loading" no-wrap fixed></b-overlay>
      <b-col>
        <h1>Modifier: {{ modifier.name }}</h1>
      </b-col>
      <b-col class="text-right">
        <saved-notice v-if="saved" />
        <b-button
          v-if="!readOnly"
          class="mb-1 ml-2"
          variant="primary"
          @click="onSaveClick"
        >
          Save
        </b-button>
        <b-button
          class="mb-1 ml-2"
          variant="primary"
          @click="$router.push('/modifiers')"
        >
          Back
        </b-button>
      </b-col>
    </b-row>
    <error-alert :errors="errors" class="mb-3" />
    <b-row class="base-form mb-3 mx-0">
      <b-col lg="6" sm="12">
        <b-form-group label="Name">
          <b-form-input v-model="modifier.name" :disabled="readOnly" />
        </b-form-group>
        <b-form-group label="Key">
          <b-form-input v-model="modifier.import_key" :disabled="readOnly" />
        </b-form-group>
        <b-form-group label="Type">
          <b-form-select
            v-model="modifier.type"
            :disabled="readOnly"
            :options="typeOptions"
            @change="onUpdateOptions"
          ></b-form-select>
        </b-form-group>
      </b-col>
      <b-col lg="6" sm="12">
        <b-row class="mt-0 mt-sm-3">
          <b-col class="mt-0 mt-sm-3">
            <b-form-checkbox
              v-model="modifier.required"
              :disabled="readOnly"
              :value="1"
              :unchecked-value="0"
            >
              Required?
            </b-form-checkbox>
          </b-col>
        </b-row>
        <custom-search-field
          v-model="modifier.brand"
          :disabled="readOnly"
          :placeholder="'Select brand'"
          :path-to-search="'brands'"
          :group-label="'Brand'"
          class="mt-4"
        />
        <b-form-group label="Maximum">
          <b-form-input
            id="Maximum"
            v-model="modifier.maximum"
            :disabled="readOnly"
            type="number"
            min="0"
            @change="onUpdateOptions"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row class="mb-3 overflow-auto" no-gutters>
      <table class="editable-table">
        <thead>
          <tr>
            <th class="info-holder">
              <img
                id="modifier-options"
                :src="asset('/imgs/icons/info.svg')"
                alt="hours info"
              />
            </th>
            <th style="min-width: 220px" class="text-left pl-2 ml-1">
              Modifier options
            </th>
            <b-tooltip target="modifier-options" triggers="hover">
              Hover over existing modifier option rows to edit their name,
              price, and pre-selected status.
            </b-tooltip>
            <th width="12%" class="pl-3">Price</th>
            <th width="5%" class="text-center">Preselected?</th>
            <th width="4%" />
          </tr>
        </thead>
        <draggable
          v-if="modifier.attribute_items.length"
          :list="modifier.attribute_items"
          tag="tbody"
          :disabled="readOnly"
        >
          <tr
            v-for="(element, methodIndex) in modifier.attribute_items"
            :key="element.id"
          >
            <td class="text-center">
              <b-icon v-if="!readOnly" icon="list"></b-icon>
            </td>
            <td class="py-0" style="width: 50%">
              <b-form-input
                v-model="element.name"
                :disabled="readOnly"
                class="single-input form-control"
              />
            </td>
            <td width="12%" style="min-width: 95px" class="py-0 text-center">
              <currency-input
                v-model="element.price"
                :disabled="readOnly"
                class="single-input form-control"
              />
            </td>
            <td class="text-center py-0">
              <b-form-checkbox
                :checked="element.preselected"
                :disabled="readOnly"
                value="1"
                unchecked-value="0"
                class="mt-2"
                @change="updatePreselectedList(element, element.preselected)"
              />
            </td>
            <td class="py-0">
              <b-button
                v-if="!readOnly"
                variant="white"
                title="Remove Menu Category"
                :method-index="methodIndex"
                class="text-center text-primary"
                @click="removeModifierItem"
              >
                <b-icon icon="x"></b-icon>
              </b-button>
            </td>
          </tr>
        </draggable>
        <tbody
          v-if="
            !readOnly &&
            (modifier.maximum < modifier.attribute_items.length + 1 ||
              modifier.maximum == 0)
          "
        >
          <tr>
            <td />
            <td class="py-0">
              <b-form-input
                v-model="newModifier"
                autocomplete="off"
                class="single-input w-100"
                placeholder="Type and click enter to add modifier options..."
                @keyup.enter.native="addModifierItem"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </b-row>
    <b-row class="mb-4 overflow-auto">
      <items ref="items" :modifier-id="modifier.id" />
    </b-row>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import draggable from 'vuedraggable';
import Modifier from '../../models/Modifier.js';
import SavedNotice from '../../components/SavedNotice';
import Items from './Items';

export default {
  name: 'Modifier',
  components: {
    draggable,
    Items,
    SavedNotice,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      saved: false,
      selected: null,
      brand: null,
      newModifier: null,
      modifier: new Modifier({
        id: this.$route.params.id,
      }),
      preselectedState: [],
      items: [],
      errors: {},
      brandRaw: null,
      typeOptions: [
        {
          value: 'select',
          text: 'Select one',
        },
        {
          value: 'multi-select',
          text: 'Select many',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('session', ['brandIds']),
    readOnly() {
      if (this.modifier && !this.brandIds.includes(this.modifier.brand_id)) {
        return true;
      }

      return false;
    },
  },
  mounted() {
    this.refreshRecord();
  },
  methods: {
    updatePreselectedList(item, value) {
      const index = this.modifier.attribute_items.findIndex(
        (el) => el.id === item.id
      );
      if (this.modifier.type === 'select') {
        for (let i = 0; i < this.modifier.attribute_items.length; i += 1) {
          this.modifier.attribute_items[i].preselected = 0;
        }

        // Eslint throws error but works like should
        // this.modifier.attribute_items.forEach((element) => {
        //   element.preselected = 0;
        // });

        this.modifier.attribute_items[index].preselected = +!value;
      } else {
        // Create temporary array and fill it with items which preselected prop = 1
        const tempArray = this.modifier.attribute_items.filter(
          (element) => element.preselected === 1
        );
        // if maximum = 0 you can pick any count of checkboxes
        if (!this.modifier.maximum) {
          this.modifier.attribute_items[index].preselected = +!value;
        } else {
          // if tempArray length >= modifier maximum changle last item in array to 0
          for (let i = tempArray.length; i >= this.modifier.maximum; i -= 1) {
            const tempIndex = this.modifier.attribute_items.findIndex(
              (el) => el.id === tempArray[i - 1].id
            );
            this.modifier.attribute_items[tempIndex].preselected = 0;
            // But if this is unchecking checkbox while tempArray.length = modifier.maximun
            // return last item in array to 0
            if (
              i === parseInt(this.modifier.maximum, 10) &&
              this.modifier.attribute_items[index].preselected === 1
            ) {
              this.modifier.attribute_items[tempIndex].preselected = 1;
            }
          }

          this.modifier.attribute_items[index].preselected = +!value;
        }
      }
    },
    onUpdateOptions() {
      // Get first item with preselected option
      const index = this.modifier.attribute_items.findIndex(
        (el) => el.preselected === 1
      );
      if (index >= 0) {
        this.updatePreselectedList(this.modifier.attribute_items[index], false);
      }
    },
    refreshRecord() {
      const me = this;

      me.loading = true;
      const modifierRaw = new Modifier({
        id: me.$props.id,
      });
      modifierRaw
        .fetch()
        .then(() => {
          me.loading = false;
          me.modifier = modifierRaw;
        })
        .catch((error) => {
          alert(error);
        });
    },
    onSaveClick() {
      const me = this;
      let i;

      me.loading = true;

      for (i = 0; i < me.modifier.attribute_items.length; i += 1) {
        me.modifier.attribute_items[i].priority = i;
      }

      me.modifier
        .save()
        .then((response) => {
          me.loading = false;

          if (!response.getData().success) {
            me.saved = false;
            me.errors = response.getData().error;
            return;
          }
          me.saved = true;
          setTimeout(() => {
            me.saved = false;
          }, 5000);
          me.errors = {};
          me.refreshRecord();
        })
        .catch((error) => {
          alert(error);
        });
    },
    removeModifierItem(event) {
      this.modifier.attribute_items.splice(
        Number(event.currentTarget.getAttribute('method-index')),
        1
      );
    },
    addModifierItem() {
      this.modifier.attribute_items.push({
        name: this.newModifier,
        preselected: 0,
        priority: 0,
        attribute_id: 0,
        price: 0,
      });
      this.newModifier = null;
    },
  },
};
</script>
